import React, { useState, useRef, useEffect } from "react";
import {
  MONDAY,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY,
  SUNDAY,
  DAY_SLASH_TIME,
  DRAG_THE_MOUSE_TO_SELECT_THE_TIME_PERIOD,
  SELECTED_TIME_PERIOD,
  NO_SELECTION,
  BUTTON_CLEAR_ALL,
  ADS_SCHEDULE_PLACEHOLDER,
  TIMEZONE_PLACEHOLDER,
  USER_TIMEZONE,
  AD_ACCOUNT_TIMEZONE,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "../../../../components/Buttons/Button";
import { useController, useFormContext } from "react-hook-form";
import { BORDER_RADIUS, META } from "../../../../utils/constants";
import Switch from "../../../../components/Form/Switch";
import _ from "lodash";
import Select from "../../../../components/Form/Select";
import { useTheme } from "@mui/material";
import { getReactHookFormNestedError } from "../../../../utils/errors";

// Days of the week (Monday to Sunday)
const daysOfWeek = [
  MONDAY,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY,
  SUNDAY,
];
// Hours of the day (0 to 23)
const hours = Array.from({ length: 24 }, (_, i) => i);

// Helper to create initial selection state (7 days x 24 hours)
const createInitialSelection = () =>
  Array(7)
    .fill(null)
    .map(() => Array(24).fill(false));

// Helper to format hour to HH:MM
const formatHour = (hour) => `${hour.toString().padStart(2, "0")}:00`;
// Helper to format the end hour as HH:59 (end is exclusive)
const formatEndHourExclusive = (endHour) => {
  const hourToShow = Math.max(0, endHour - 1);
  return `${hourToShow.toString().padStart(2, "0")}:59`;
};
// Helper to format half-hour (for future use)

// Get intervals for each day from selection
function getDayIntervals(daySelection) {
  const intervals = [];
  let start = null;
  for (let i = 0; i < daySelection.length; i++) {
    if (daySelection[i] && start === null) {
      start = i;
    }
    if ((!daySelection[i] || i === daySelection.length - 1) && start !== null) {
      const end = daySelection[i] ? i + 1 : i;
      intervals.push([start, end]);
      start = null;
    }
  }
  return intervals;
}
/**
 * Groups the 7x24 selection into compact blocks as expected by Meta.
 * @param {boolean[][]} selection - 7x24 matrix of boolean values
 * @returns {Array} [{ days, startMinute, endMinute, timezone_type }]
 */
function formatSelectionToSchedule(selection) {
  // 1. For each day, find the intervals of selected hours
  const dayIntervals = selection.map((daySel) => {
    const intervals = [];
    let start = null;
    for (let h = 0; h < 24; h++) {
      if (daySel[h] && start === null) start = h;
      if ((!daySel[h] || h === 23) && start !== null) {
        const end = daySel[h] ? h + 1 : h;
        intervals.push([start * 60, end * 60]);
        start = null;
      }
    }
    return intervals;
  });

  // 2. Group days that have the same intervals
  const intervalMap = {};
  dayIntervals.forEach((intervals, dayIdx) => {
    for (const [start, end] of intervals) {
      const key = `${start}-${end}`;
      if (!intervalMap[key]) intervalMap[key] = [];
      intervalMap[key].push(dayIdx);
    }
  });

  // 3. Build the result
  const result = [];
  for (const key in intervalMap) {
    const [startMinute, endMinute] = key.split("-").map(Number);
    result.push({
      days: intervalMap[key],
      startMinute,
      endMinute,
    });
  }
  return result;
}

/**
 * Converts a compact schedule array to a 7x24 boolean selection matrix.
 * @param {Array} schedule - Array of { days, start, end }
 * @returns {boolean[][]} 7x24 selection matrix
 */
const formatScheduleToSelection = (schedule) => {
  const selection = Array(7)
    .fill(null)
    .map(() => Array(24).fill(false));

  if (!Array.isArray(schedule)) return selection;

  for (const block of schedule) {
    const { days, startMinute, endMinute } = block;
    if (
      !Array.isArray(days) ||
      typeof startMinute !== "number" ||
      typeof endMinute !== "number"
    )
      continue;
    const startHour = Math.floor(startMinute / 60);
    const endHour = Math.ceil(endMinute / 60);
    days.forEach((day) => {
      for (let hour = startHour; hour < endHour; hour++) {
        if (hour >= 0 && hour < 24 && day >= 0 && day < 7) {
          selection[day][hour] = true;
        }
      }
    });
  }
  return selection;
};

const WeeklyTimeSelector = ({
  label,
  name,
  control,
  platform,
  disabled,
  sxSelect = {},
  errors = {},
  disabledSwitch = false,
}) => {
  const isInitialized = useRef(false);
  const { setValue } = useFormContext();
  const { field } = useController({ name, control });
  const [isEnabledSchedule, setIsEnabledSchedule] = useState(
    !_.isEmpty(field?.value?.intervals)
  );
  const [selection, setSelection] = useState(
    formatScheduleToSelection(field?.value?.intervals)
  );
  const [previewSelection, setPreviewSelection] = useState(null);
  const isSelecting = useRef(false);
  const selectionMode = useRef(true);
  const startCell = useRef(null);

  const theme = useTheme();
  const { t } = useTranslationApp();
  const error = getReactHookFormNestedError(errors, name);

  // Helper to get the rectangle area between two cells
  const getRectArea = (cellA, cellB) => {
    const minDay = Math.min(cellA.day, cellB.day);
    const maxDay = Math.max(cellA.day, cellB.day);
    const minHour = Math.min(cellA.hour, cellB.hour);
    const maxHour = Math.max(cellA.hour, cellB.hour);
    return { minDay, maxDay, minHour, maxHour };
  };

  // Update selection state for a rectangle
  const onRectSelection = (cellA, cellB, value) => {
    if (disabled) return;
    const { minDay, maxDay, minHour, maxHour } = getRectArea(cellA, cellB);
    setPreviewSelection(() => {
      const newSel = selection.map((row) => [...row]);
      for (let d = minDay; d <= maxDay; d++) {
        for (let h = minHour; h <= maxHour; h++) {
          newSel[d][h] = value;
        }
      }
      return newSel;
    });
  };

  // Mouse down on a cell
  const onMouseDown = (dayIdx, hourIdx) => {
    if (disabled) return;
    isSelecting.current = true;
    selectionMode.current = !selection[dayIdx][hourIdx];
    startCell.current = { day: dayIdx, hour: hourIdx };
    onRectSelection(
      startCell.current,
      startCell.current,
      selectionMode.current
    );
  };

  // Mouse over a cell while dragging
  const onMouseOver = (dayIdx, hourIdx) => {
    if (disabled) return;
    if (isSelecting.current && startCell.current) {
      onRectSelection(
        startCell.current,
        { day: dayIdx, hour: hourIdx },
        selectionMode.current
      );
    }
  };

  // Mouse up (end selection)
  const onMouseUp = () => {
    if (disabled) return;
    if (isSelecting.current && previewSelection) {
      setSelection(previewSelection);
    }
    isSelecting.current = false;
    startCell.current = null;
    setPreviewSelection(null);
  };

  // Mouse leave (in case mouse leaves the grid)
  const onMouseLeave = () => {
    if (disabled) return;
    isSelecting.current = false;
    startCell.current = null;
    setPreviewSelection(null);
  };

  // Clear all selection
  const onClearAll = () => {
    setSelection(createInitialSelection());
    setPreviewSelection(null);
  };

  const onChangeIsEnabledSchedule = (value) => {
    setIsEnabledSchedule(value);
    if (!value) {
      setSelection(createInitialSelection());
      field.onChange({ intervals: [], timezone: "" });
    }
  };

  // Build summary of selected intervals
  const summary = (previewSelection || selection).map((daySel) =>
    getDayIntervals(daySel)
  );

  // Set default value only once on mount if field is empty, without marking as dirty
  useEffect(() => {
    setValue(
      name,
      {
        intervals: formatSelectionToSchedule(selection),
        timezone: "user",
      },
      { shouldDirty: false }
    );
    setTimeout(() => {
      isInitialized.current = true;
    }, 1000);
    // eslint-disable-next-line
  }, []);

  // Update value every time selection changes, but only if field is not empty
  useEffect(() => {
    if (!isInitialized.current) return;
    field.onChange({
      intervals: formatSelectionToSchedule(selection),
      timezone: "user",
    });
    // eslint-disable-next-line
  }, [selection]);

  return (
    <Box
      id={name}
      sx={{
        width: "100%",
      }}
    >
      {label && (
        <Typography variant="body1" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}
      {error && (
        <Typography variant="caption" sx={{ color: "error.main" }}>
          {error.message}
        </Typography>
      )}
      <Switch
        isActive={isEnabledSchedule}
        onChangeIsActive={onChangeIsEnabledSchedule}
        labelActive={ADS_SCHEDULE_PLACEHOLDER}
        labelDeactivate={ADS_SCHEDULE_PLACEHOLDER}
        disabled={disabled || disabledSwitch}
      />

      {isEnabledSchedule && (
        <Box
          sx={{
            userSelect: "none",
            borderRadius: BORDER_RADIUS,
            width: "100%",
            mt: 1,
          }}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
        >
          {platform === META && (
            <Select
              disabled={disabled}
              variant="filled"
              fullWidth
              label={t(TIMEZONE_PLACEHOLDER)}
              options={[
                {
                  label: t(USER_TIMEZONE),
                  value: "user",
                },
                {
                  label: t(AD_ACCOUNT_TIMEZONE),
                  value: "adAccount",
                },
              ]}
              sx={{
                mb: 2,
                ...sxSelect,
              }}
              name={`${name}.timezone`}
              control={control}
            />
          )}
          <Box
            sx={{
              border: `1px solid ${theme?.palette?.fields?.placeholder}`,
              borderRadius: BORDER_RADIUS,
              overflow: "hidden",
              backgroundColor: "background.paper",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "100px repeat(24, 1fr)",
                gridTemplateRows: "auto auto repeat(7, 40px)",
                userSelect: "none",
              }}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseLeave}
            >
              {/* Day/Time header */}
              <Box
                sx={{
                  gridRow: "1 / 3",
                  gridColumn: "1",
                  backgroundColor: "grey.100",
                  borderRight: `1px solid ${theme?.palette?.fields?.placeholder}`,
                  borderBottom: `1px solid ${theme?.palette?.fields?.placeholder}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                  }}
                >
                  {t(DAY_SLASH_TIME)}
                </Typography>
              </Box>

              {/* Time period headers */}
              <Box
                sx={{
                  gridRow: "1",
                  gridColumn: "2 / 14",
                  backgroundColor: "grey.100",
                  borderRight: `1px solid ${theme?.palette?.fields?.placeholder}`,
                  borderBottom: `1px solid ${theme?.palette?.fields?.placeholder}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                  }}
                >
                  00:00 - 12:00
                </Typography>
              </Box>

              <Box
                sx={{
                  gridRow: "1",
                  gridColumn: "14 / 26",
                  backgroundColor: "grey.100",
                  borderBottom: `1px solid ${theme?.palette?.fields?.placeholder}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                  }}
                >
                  12:00 - 24:00
                </Typography>
              </Box>

              {/* Hour headers */}
              {hours.map((hour, hourIdx) => {
                const isLastColumn = hourIdx === hours.length - 1;
                return (
                  <Box
                    key={hour}
                    sx={{
                      gridRow: "2",
                      gridColumn: `${hour + 2}`,
                      backgroundColor: "grey.100",
                      borderRight: isLastColumn
                        ? "none"
                        : `1px solid ${theme?.palette?.fields?.placeholder}`,
                      borderBottom: `1px solid ${theme?.palette?.fields?.placeholder}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        color: "text.secondary",
                      }}
                    >
                      {hour.toString().padStart(2, "0")}
                    </Typography>
                  </Box>
                );
              })}

              {/* Day rows */}
              {daysOfWeek.map((day, dayIdx) => (
                <React.Fragment key={day}>
                  {/* Day label */}
                  <Box
                    sx={{
                      gridRow: `${dayIdx + 3}`,
                      gridColumn: "1",
                      backgroundColor: "grey.100",
                      borderRight: `1px solid ${theme?.palette?.fields?.placeholder}`,
                      borderBottom:
                        dayIdx === daysOfWeek.length - 1
                          ? "none"
                          : `1px solid ${theme?.palette?.fields?.placeholder}`,
                      display: "flex",
                      alignItems: "center",
                      px: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                      }}
                    >
                      {t(day)}
                    </Typography>
                  </Box>

                  {/* Hour cells */}
                  {hours.map((hour, hourIdx) => {
                    const isSelected = (previewSelection || selection)[dayIdx][
                      hourIdx
                    ];
                    const isLastColumn = hourIdx === hours.length - 1;
                    return (
                      <Box
                        key={`${dayIdx}-${hourIdx}`}
                        sx={{
                          gridRow: `${dayIdx + 3}`,
                          gridColumn: `${hourIdx + 2}`,
                          borderRight: isLastColumn
                            ? "none"
                            : `1px solid ${theme?.palette?.fields?.placeholder}`,
                          borderBottom: `1px solid ${theme?.palette?.fields?.placeholder}`,
                          cursor: "pointer",
                          transition: "all 0.1s ease",
                          backgroundColor: isSelected
                            ? "primary.main"
                            : "background.paper",
                          color: isSelected
                            ? "primary.contrastText"
                            : "text.primary",
                          "&:hover": {
                            backgroundColor: isSelected
                              ? "primary.dark"
                              : "grey.100",
                          },
                          ...(dayIdx === daysOfWeek.length - 1 && {
                            borderBottom: "none",
                          }),
                        }}
                        onMouseDown={() => onMouseDown(dayIdx, hourIdx)}
                        onMouseOver={() => onMouseOver(dayIdx, hourIdx)}
                      />
                    );
                  })}
                </React.Fragment>
              ))}
            </Box>

            {/* Instructions */}
            <Box
              sx={{
                p: 2,
                backgroundColor: "grey.50",
                borderTop: `1px solid ${theme?.palette?.fields?.placeholder}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t(DRAG_THE_MOUSE_TO_SELECT_THE_TIME_PERIOD)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {t(SELECTED_TIME_PERIOD)}
              </Typography>
              {!disabled &&
                !summary.every((intervals) => intervals.length === 0) && (
                  <Button color="secondary" onClick={onClearAll}>
                    {t(BUTTON_CLEAR_ALL)}
                  </Button>
                )}
            </Box>

            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}
            >
              {summary.every((intervals) => intervals.length === 0) ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontStyle: "italic",
                    py: 1,
                    textAlign: "left",
                  }}
                >
                  {t(NO_SELECTION)}
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {daysOfWeek.map((day, idx) =>
                    summary[idx].length > 0 ? (
                      <Box
                        key={day}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.85rem",
                            color: "text.primary",
                            minWidth: 80,
                            flexShrink: 0,
                          }}
                        >
                          {t(day)}
                        </Typography>
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {summary[idx].map(([start, end], intervalIdx) => (
                            <Box
                              key={intervalIdx}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                px: 1,
                                py: 0.5,
                                backgroundColor: (theme) =>
                                  `${theme.palette.primary.main}1A`,
                                color: "primary.main",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                borderRadius: BORDER_RADIUS,
                                border: (theme) =>
                                  `1px solid ${theme.palette.primary.main}33`,
                              }}
                            >
                              {`${formatHour(start)} – ${formatEndHourExclusive(end)}`}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ) : null
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default WeeklyTimeSelector;
